import { Injectable } from '@angular/core';
import { Storage, ref, uploadBytes, getDownloadURL, deleteObject, listAll, getMetadata, updateMetadata } from '@angular/fire/storage';
import { Observable, from, throwError } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { NGXLogger } from 'ngx-logger';
import { getFirebaseConfigInfo } from '../../../../environments/firebase.config';

export interface FileUploadResult {
  url: string;
  path: string;
  name: string;
  size: number;
  contentType: string;
  uploadedAt: Date;
  metadata: { [key: string]: string };
}

export interface FileMetadata {
  name: string;
  size: number;
  contentType: string;
  uploadedAt: Date;
  lastModified: Date;
  path: string;
  url?: string;
}

export interface StorageStats {
  totalFiles: number;
  totalSize: number;
  filesByType: { [contentType: string]: number };
  lastUpload: Date | null;
}

@Injectable({
  providedIn: 'root'
})
export class FirebaseStorageService {
  private readonly STORAGE_PATHS = {
    FASCICOLI: 'fascicoli',
    DOMANDE_SOSTEGNO: 'fascicoli/domande-sostegno',
    DOMANDE_PAGAMENTO: 'fascicoli/domande-pagamento',
    TEMP: 'temp',
    BACKUP: 'backup'
  };

  constructor(
    private storage: Storage,
    private logger: NGXLogger
  ) {
    // Log della configurazione Storage all'inizializzazione
    const configInfo = getFirebaseConfigInfo();
/*     this.logger.info('[FIREBASE-STORAGE-SERVICE] Inizializzazione servizio Storage:', {
      projectId: configInfo.projectId,
      environment: configInfo.environment,
      configValid: configInfo.isValid
    }); */
  }

  /**
   * Carica un file su Firebase Storage
   */
  uploadFile(
    file: File,
    path: string,
    metadata?: { [key: string]: string },
    /** Se impostato, nome oggetto su storage (es. numero in chiaro + estensione). */
    storageFileName?: string,
  ): Observable<FileUploadResult> {
    const objectName = storageFileName?.trim() || file.name;
    const folder = path.replace(/\/+$/, '');
    const fullPath = `${folder}/${objectName}`;

    this.logger.info('[FIREBASE-STORAGE] Upload file:', {
      originalName: file.name,
      storageName: objectName,
      size: file.size,
      path: folder,
    });

    const storageRef = ref(this.storage, fullPath);
    const uploadTask = uploadBytes(storageRef, file, {
      customMetadata: {
        originalName: file.name,
        storageFileName: objectName,
        uploadedAt: new Date().toISOString(),
        ...metadata
      }
    });

    return from(uploadTask).pipe(
      switchMap(async (snapshot) => {
        const downloadURL = await getDownloadURL(snapshot.ref);
        const fileMetadata = await getMetadata(snapshot.ref);

        const result: FileUploadResult = {
          url: downloadURL,
          path: snapshot.ref.fullPath,
          name: objectName,
          size: file.size,
          contentType: file.type,
          uploadedAt: new Date(),
          metadata: metadata || {}
        };

        this.logger.info('[FIREBASE-STORAGE] File caricato con successo:', result);
        return result;
      }),
      catchError(error => {
        this.logger.error('[FIREBASE-STORAGE] Errore upload file:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Carica un file PDF per un fascicolo specifico
   */
  uploadFascicoloFile(
    file: File,
    fascicoloId: string,
    tipo: 'domanda-sostegno' | 'domanda-pagamento',
    metadata?: { [key: string]: string }
  ): Observable<FileUploadResult> {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const fileName = `${fascicoloId}_${tipo}_${timestamp}.pdf`;
    const folder = `${this.STORAGE_PATHS.FASCICOLI}/${fascicoloId}/${tipo}`;

    return this.uploadFile(
      file,
      folder,
      {
        fascicoloId,
        tipo,
        ...metadata,
      },
      fileName,
    );
  }

  /**
   * Ottiene l'URL di download di un file
   */
  getDownloadURL(path: string): Observable<string> {
    this.logger.info('[FIREBASE-STORAGE] Ottenimento URL download:', path);

    const storageRef = ref(this.storage, path);
    return from(getDownloadURL(storageRef)).pipe(
      map(url => {
        this.logger.info('[FIREBASE-STORAGE] URL download ottenuto:', url);
        return url;
      }),
      catchError(error => {
        this.logger.error('[FIREBASE-STORAGE] Errore ottenimento URL:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Scarica un file da Firebase Storage forzando il nome file desiderato
   */
  downloadFile(path: string, fileName: string): Observable<void> {
    this.logger.info('[FIREBASE-STORAGE] Download file richiesto:', { path, fileName });

    return this.getDownloadURL(path).pipe(
      switchMap((url) => from(fetch(url))),
      switchMap((response) => {
        if (!response.ok) {
          return throwError(() => new Error(`Download fallito con status ${response.status}`));
        }
        return from(response.blob());
      }),
      map((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = fileName || 'download';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(objectUrl);

        this.logger.info('[FIREBASE-STORAGE] Download avviato con successo:', { path, fileName });
      }),
      catchError(error => {
        this.logger.error('[FIREBASE-STORAGE] Errore durante il download del file:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Elimina un file da Firebase Storage
   */
  deleteFile(path: string): Observable<void> {
    this.logger.info('[FIREBASE-STORAGE] Eliminazione file:', path);

    const storageRef = ref(this.storage, path);
    return from(deleteObject(storageRef)).pipe(
      map(() => {
        this.logger.info('[FIREBASE-STORAGE] File eliminato con successo:', path);
      }),
      catchError(error => {
        this.logger.error('[FIREBASE-STORAGE] Errore eliminazione file:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Lista tutti i file in una directory
   */
  listFiles(path: string): Observable<FileMetadata[]> {
    this.logger.info('[FIREBASE-STORAGE] Lista file in directory:', path);

    const storageRef = ref(this.storage, path);
    return from(listAll(storageRef)).pipe(
      switchMap(async (result) => {
        const files: FileMetadata[] = [];

        for (const item of result.items) {
          try {
            const metadata = await getMetadata(item);
            const downloadURL = await getDownloadURL(item);

            files.push({
              name: metadata.name,
              size: metadata.size,
              contentType: metadata.contentType || 'application/octet-stream',
              uploadedAt: new Date(metadata.timeCreated),
              lastModified: new Date(metadata.updated),
              path: item.fullPath,
              url: downloadURL
            });
          } catch (error) {
            this.logger.warn('[FIREBASE-STORAGE] Errore ottenimento metadata per file:', item.name, error);
          }
        }

        this.logger.info('[FIREBASE-STORAGE] File trovati:', files.length);
        return files;
      }),
      catchError(error => {
        this.logger.error('[FIREBASE-STORAGE] Errore lista file:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Lista i file di un fascicolo specifico
   */
  listFascicoloFiles(fascicoloId: string): Observable<{
    domandeSostegno: FileMetadata[];
    domandePagamento: FileMetadata[];
  }> {
    this.logger.info('[FIREBASE-STORAGE] Lista file fascicolo:', fascicoloId);

    const domandeSostegnoPath = `${this.STORAGE_PATHS.FASCICOLI}/${fascicoloId}/domanda-sostegno`;
    const domandePagamentoPath = `${this.STORAGE_PATHS.FASCICOLI}/${fascicoloId}/domanda-pagamento`;

    return from(Promise.all([
      this.listFiles(domandeSostegnoPath).toPromise().catch(() => []),
      this.listFiles(domandePagamentoPath).toPromise().catch(() => [])
    ])).pipe(
      map(([domandeSostegno, domandePagamento]) => ({
        domandeSostegno: domandeSostegno || [],
        domandePagamento: domandePagamento || []
      }))
    );
  }

  /**
   * Ottiene le statistiche dello storage
   */
  getStorageStats(): Observable<StorageStats> {
    this.logger.info('[FIREBASE-STORAGE] Calcolo statistiche storage');

    return this.listFiles(this.STORAGE_PATHS.FASCICOLI).pipe(
      map(files => {
        const stats: StorageStats = {
          totalFiles: files.length,
          totalSize: files.reduce((sum, file) => sum + file.size, 0),
          filesByType: {},
          lastUpload: null
        };

        files.forEach(file => {
          // Conta file per tipo
          const contentType = file.contentType;
          stats.filesByType[contentType] = (stats.filesByType[contentType] || 0) + 1;

          // Trova ultimo upload
          if (!stats.lastUpload || file.uploadedAt > stats.lastUpload) {
            stats.lastUpload = file.uploadedAt;
          }
        });

        this.logger.info('[FIREBASE-STORAGE] Statistiche calcolate:', stats);
        return stats;
      }),
      catchError(error => {
        this.logger.error('[FIREBASE-STORAGE] Errore calcolo statistiche:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Aggiorna i metadati di un file
   */
  updateFileMetadata(path: string, metadata: { [key: string]: string }): Observable<void> {
    this.logger.info('[FIREBASE-STORAGE] Aggiornamento metadata file:', path);

    const storageRef = ref(this.storage, path);
    return from(updateMetadata(storageRef, { customMetadata: metadata })).pipe(
      map(() => {
        this.logger.info('[FIREBASE-STORAGE] Metadata aggiornati con successo:', path);
      }),
      catchError(error => {
        this.logger.error('[FIREBASE-STORAGE] Errore aggiornamento metadata:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Pulisce i file temporanei
   */
  cleanupTempFiles(): Observable<number> {
    this.logger.info('[FIREBASE-STORAGE] Pulizia file temporanei');

    return this.listFiles(this.STORAGE_PATHS.TEMP).pipe(
      switchMap(async (files) => {
        const now = new Date();
        const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

        let deletedCount = 0;

        for (const file of files) {
          if (file.uploadedAt < oneDayAgo) {
            try {
              await this.deleteFile(file.path).toPromise();
              deletedCount++;
            } catch (error) {
              this.logger.warn('[FIREBASE-STORAGE] Errore eliminazione file temp:', file.name, error);
            }
          }
        }

        this.logger.info('[FIREBASE-STORAGE] File temporanei eliminati:', deletedCount);
        return deletedCount;
      }),
      catchError(error => {
        this.logger.error('[FIREBASE-STORAGE] Errore pulizia file temp:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Ottiene i percorsi di storage predefiniti
   */
  getStoragePaths() {
    return this.STORAGE_PATHS;
  }

  /**
   * Testa la connessione a Firebase Storage (versione semplificata senza chiamate API)
   */
  async testStorageConnection(): Promise<{
    connected: boolean;
    error?: string;
    responseTime?: number;
    projectId?: string;
  }> {
    const startTime = Date.now();

    try {
      // Verifica solo la configurazione Firebase senza chiamare API
      const configInfo = getFirebaseConfigInfo();

      if (!configInfo.isValid) {
        this.logger.error('[FIREBASE-STORAGE] Configurazione Firebase non valida');
        return {
          connected: false,
          error: 'Configurazione Firebase non valida',
          projectId: configInfo.projectId
        };
      }

      // Test semplificato: verifica solo che il servizio Storage sia inizializzato
      // Non facciamo chiamate API per evitare errori di injection context
      const responseTime = Date.now() - startTime;

      // Se arriviamo qui, la configurazione è valida e il servizio è inizializzato
    /*   this.logger.info('[FIREBASE-STORAGE] ✅ Configurazione Storage valida!', {
        projectId: configInfo.projectId,
        environment: configInfo.environment,
        responseTime: `${responseTime}ms`
      }); */

      return {
        connected: true,
        responseTime,
        projectId: configInfo.projectId
      };

    } catch (error: any) {
      const responseTime = Date.now() - startTime;
      this.logger.error('[FIREBASE-STORAGE] ❌ Errore configurazione Storage:', error);

      return {
        connected: false,
        error: error.message || 'Errore sconosciuto',
        responseTime,
        projectId: getFirebaseConfigInfo().projectId
      };
    }
  }

  /**
   * Testa la connessione a Firebase Storage con chiamate API reali (da usare dopo l'inizializzazione)
   */
  async testStorageConnectionWithAPI(): Promise<{
    connected: boolean;
    error?: string;
    responseTime?: number;
    projectId?: string;
  }> {
    const startTime = Date.now();

    try {
      this.logger.info('[FIREBASE-STORAGE] Inizio test connessione Storage con API...');

      // Verifica la configurazione Firebase
      const configInfo = getFirebaseConfigInfo();

      if (!configInfo.isValid) {
        this.logger.error('[FIREBASE-STORAGE] Configurazione Firebase non valida');
        return {
          connected: false,
          error: 'Configurazione Firebase non valida',
          projectId: configInfo.projectId
        };
      }

      // Test di connessione effettivo - prova a creare un riferimento
      const testRef = ref(this.storage, 'connection-test/test-file.txt');

      // Prova a ottenere i metadati di un file di test (che potrebbe non esistere)
      try {
        await getMetadata(testRef);
        // Se arriviamo qui, il file esiste e la connessione funziona
        const responseTime = Date.now() - startTime;

        this.logger.info('[FIREBASE-STORAGE] ✅ Connessione Storage riuscita!', {
          projectId: configInfo.projectId,
          environment: configInfo.environment,
          responseTime: `${responseTime}ms`
        });

        return {
          connected: true,
          responseTime,
          projectId: configInfo.projectId
        };

      } catch (metadataError: any) {
        // Se il file non esiste, proviamo a creare un file di test
        if (metadataError.code === 'storage/object-not-found') {
          // Creiamo un file di test temporaneo
          const testContent = new Blob(['Firebase Storage Connection Test'], { type: 'text/plain' });
          const testFile = new File([testContent], 'test-connection.txt', { type: 'text/plain' });

          try {
            await uploadBytes(testRef, testFile, {
              customMetadata: {
                testConnection: 'true',
                timestamp: new Date().toISOString(),
                environment: configInfo.environment
              }
            });

            const responseTime = Date.now() - startTime;

            this.logger.info('[FIREBASE-STORAGE] ✅ Connessione Storage riuscita (file di test creato)!', {
              projectId: configInfo.projectId,
              environment: configInfo.environment,
              responseTime: `${responseTime}ms`
            });

            // Pulisci il file di test
            try {
              await deleteObject(testRef);
            } catch (cleanupError) {
              this.logger.warn('[FIREBASE-STORAGE] Errore pulizia file di test:', cleanupError);
            }

            return {
              connected: true,
              responseTime,
              projectId: configInfo.projectId
            };

          } catch (uploadError: any) {
            this.logger.error('[FIREBASE-STORAGE] ❌ Errore creazione file di test:', uploadError);
            return {
              connected: false,
              error: `Errore upload test: ${uploadError.message}`,
              projectId: configInfo.projectId
            };
          }
        } else {
          // Altro errore di metadati
          this.logger.error('[FIREBASE-STORAGE] ❌ Errore metadati file di test:', metadataError);
          return {
            connected: false,
            error: `Errore metadati: ${metadataError.message}`,
            projectId: configInfo.projectId
          };
        }
      }

    } catch (error: any) {
      const responseTime = Date.now() - startTime;
      this.logger.error('[FIREBASE-STORAGE] ❌ Errore connessione Storage:', error);

      return {
        connected: false,
        error: error.message || 'Errore sconosciuto',
        responseTime,
        projectId: getFirebaseConfigInfo().projectId
      };
    }
  }

  /**
   * Ottiene informazioni dettagliate sulla connessione Storage
   */
  getConnectionInfo(): {
    config: { isValid: boolean; projectId: string; environment: string };
    lastTest?: { connected: boolean; responseTime?: number; timestamp: Date };
  } {
    const configInfo = getFirebaseConfigInfo();

    return {
      config: configInfo,
      // Potresti aggiungere qui un campo per memorizzare l'ultimo test se necessario
    };
  }
}
